import React, { useState, useEffect, useRef } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend
} from 'recharts';
import {
  TrendingUp, ShieldCheck, Zap, Globe, Layers, Cpu
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
// Centralized Configuration
const API_CONFIG = {
  BASE_URL: 'https://api.kkbconsulting.com',
  ENDPOINTS: {
    PORTFOLIO_HISTORY: (days = 30) => `/api/portfolio/history?days=${days}`
  }
};

const C = {
  bgDark: '#0B5867',   // Deep Teal
  bgMid: '#2B6873',   // Mid Teal
  bgLight: '#9FBCC0',   // Mist
  accent: '#FF8C7A',   // Coral
  success: '#4ade80',   // Green
  danger: '#ef4444',   // Red
  white: '#FFFFFF',
  textSecondary: '#E2E8F0',
  glass: 'rgba(255, 255, 255, 0.08)',
  border: 'rgba(255, 255, 255, 0.15)',
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Inter:wght@300;400;600&display=swap');

  body { 
    margin: 0;
    font-family: 'Inter', sans-serif; 
    background-color: ${C.bgDark}; 
    color: ${C.white}; 
    overflow-x: hidden;
  }

  .main-bg {
    background: 
      radial-gradient(circle at 50% 50%, ${C.bgLight} 0%, transparent 70%),
      linear-gradient(180deg, ${C.bgDark} 0%, ${C.bgMid} 45%, ${C.bgMid} 55%, ${C.bgDark} 100%);
    background-attachment: fixed;
    min-height: 100vh;
  }

  .reveal-node {
    transition: transform 0.8s cubic-bezier(0.17, 0.67, 0.83, 0.67), opacity 0.8s ease;
    transform: perspective(1000px) rotateX(10deg) translateY(30px);
    opacity: 0;
  }
  .reveal-node.visible {
    transform: perspective(1000px) rotateX(0deg) translateY(0);
    opacity: 1;
  }
`;

// ── Shared Components ──

function EnhancedPortfolioChart({ data, loading, error }) {
  if (loading) return <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white }}>Synchronizing Market Feeds...</div>;
  if (error) return <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.accent }}>Analytics Offline</div>;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#164E58', border: `1px solid ${C.border}`, padding: '15px', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
          <p style={{ margin: '0 0 10px', fontWeight: 'bold', color: C.white }}>{label}</p>
          <p style={{ margin: '5px 0', color: C.white, opacity: 0.8 }}>Purchase: ₹{d.purchaseL} L</p>
          <p style={{ margin: '5px 0', color: C.white, opacity: 0.8 }}>Current: ₹{d.currentL} L</p>
          <p style={{ margin: '10px 0 0', color: d.isProfit ? C.success : C.danger, fontWeight: 'bold' }}>
            {d.isProfit ? 'Gain' : 'Loss'}: ₹{Math.abs(d.total_unrealized_gain / 100000).toFixed(2)} L
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%', height: 450, marginTop: '20px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
          <XAxis
            dataKey="displayDate"
            axisLine={false}
            tickLine={false}
            tick={{ fill: C.white, opacity: 0.7, fontSize: 12 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: C.white, opacity: 0.7, fontSize: 12 }}
            tickFormatter={(v) => `₹${v}L`}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
          <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ paddingBottom: '30px' }} />

          <Bar
            name="Purchase Basis"
            dataKey="purchaseL"
            fill="rgba(255,255,255,0.15)"
            radius={[4, 4, 0, 0]}
            barSize={40}
          />

          <Line
            name="Market Value"
            type="monotone"
            dataKey="currentL"
            stroke={C.white}
            strokeWidth={3}
            dot={(props) => {
              const { cx, cy, payload } = props;
              // FIX: Added 'key' prop to the circle to resolve console warning
              return (
                <circle
                  key={`dot-${payload.date}`}
                  cx={cx}
                  cy={cy}
                  r={6}
                  fill={payload.isProfit ? C.success : C.danger}
                  stroke={C.bgDark}
                  strokeWidth={2}
                />
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function CapabilityCard({ title, desc, icon: Icon, index }) {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`reveal-node ${isVisible ? 'visible' : ''}`}
      style={{
        background: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${C.border}`,
        borderRadius: '32px',
        padding: '48px',
        transitionDelay: isVisible ? `${index * 100}ms` : '0ms'
      }}
    >
      <div style={{ color: C.accent, marginBottom: '24px' }}>
        <Icon size={40} />
      </div>
      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', marginBottom: '16px', color: C.white }}>{title}</h3>
      <p style={{ color: C.white, opacity: 0.7, lineHeight: 1.7 }}>{desc}</p>
    </div>
  );
}

function StatItem({ value, suffix, label, active }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) { setCount(0); return; }
    let start = 0;
    const end = parseInt(value);
    const timer = setInterval(() => {
      start += Math.ceil(end / 50);
      if (start >= end) { setCount(end); clearInterval(timer); }
      else { setCount(start); }
    }, 30);
    return () => clearInterval(timer);
  }, [active, value]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '3.5rem', fontWeight: 900, color: C.white }}>
        {count}{suffix}
      </div>
      <div style={{ color: C.white, opacity: 0.5, fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginTop: '8px' }}>
        {label}
      </div>
    </div>
  );
}

// ── Main Home Component ──

const Home = () => {
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);
  const [portfolioData, setPortfolioData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestDeployed, setLatestDeployed] = useState("2400");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PORTFOLIO_HISTORY(30)}`, {
          headers: { 'accept': 'application/json' }
        });
        if (!response.ok) throw new Error('Network error');
        const rawData = await response.json();

        const processed = rawData
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(-10)
          .map(item => ({
            ...item,
            id: item.date, // Unique identifier for mapping
            displayDate: new Date(item.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
            purchaseL: (item.total_purchase / 100000).toFixed(2),
            currentL: (item.total_current_value / 100000).toFixed(2),
            profitVal: item.total_unrealized_gain,
            isProfit: item.total_unrealized_gain >= 0
          }));

        setPortfolioData(processed);
        if (processed.length > 0) {
          const latest = processed[processed.length - 1];
          setLatestDeployed(Math.floor(parseFloat(latest.purchaseL)).toString());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);

    const observer = new IntersectionObserver(([entry]) => {
      setStatsVisible(entry.isIntersecting);
    }, { threshold: 0.2 });

    if (statsRef.current) observer.observe(statsRef.current);

    return () => {
      style.remove();
      observer.disconnect();
    };
  }, []);

  const capabilities = [
    { title: 'Capital Strategy', desc: 'Precision deployment of internal reserves across high-growth markets.', icon: TrendingUp },
    { title: 'Tech Ventures', desc: 'Accelerating frontier technologies with architectural funding.', icon: Cpu },
    { title: 'Portfolio Mgmt', desc: 'Institutional-grade asset allocation for sustainable scaling.', icon: Layers },
    { title: 'Corporate Advisory', desc: 'Strategic C-level guidance for capital structure and exits.', icon: ShieldCheck },
    { title: 'Global Markets', desc: 'Accessing international liquidity and cross-border opportunities.', icon: Globe },
    { title: 'Risk Engineering', desc: 'Sophisticated data signals to maintain strict capital preservation.', icon: Zap },
  ];

  return (
    <div className="main-bg">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section style={{ padding: '220px 30px 100px', textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(3rem, 8vw, 6rem)', lineHeight: 1.1, marginBottom: '30px', color: C.white }}>
          Precision Capital. <br /> Visionary Growth.
        </h1>
        <p style={{ fontSize: '1.2rem', color: C.white, opacity: 0.8, maxWidth: 600, margin: '0 auto 50px', lineHeight: 1.6 }}>
          Deploying proprietary intelligence and strategic capital to architect the next frontier of high-impact enterprises.
        </p>
        <button style={{ background: 'transparent', border: `2px solid ${C.white}`, color: C.white, padding: '18px 45px', borderRadius: '40px', fontWeight: 600, cursor: 'pointer' }}>
          Explore Our Capability
        </button>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} style={{ padding: '80px 30px' }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '40px',
          background: 'rgba(255,255,255,0.02)', padding: '60px', borderRadius: '40px', border: `1px solid ${C.border}`
        }}>
          <StatItem value={latestDeployed} suffix="00000+" label="Deployed" active={statsVisible} />
          <StatItem value="5" suffix="+" label="Enterprises" active={statsVisible} />
          <StatItem value="12" suffix="%" label="Avg Return" active={statsVisible} />
          <StatItem value="3" suffix="yr" label="Track Record" active={statsVisible} />
        </div>
      </section>

      {/* Investment Trend Section */}
      <section style={{ padding: '100px 30px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          background: 'rgba(255,255,255,0.02)', padding: '50px', borderRadius: '40px', border: `1px solid ${C.border}`
        }}>
          <div style={{ marginBottom: '40px' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.8rem', color: C.white, marginBottom: '10px' }}>Portfolio Momentum</h2>
            <p style={{ color: C.white, opacity: 0.6 }}>Real-time analysis of deployment vs. current market performance (₹ Lakhs).</p>
          </div>
          <EnhancedPortfolioChart data={portfolioData} loading={loading} error={error} />
        </div>
      </section>

      {/* Capabilities Section */}
      <section style={{ padding: '100px 30px', maxWidth: 1300, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '3.5rem', color: C.white, marginBottom: '20px' }}>Capabilities</h2>
          <p style={{ color: C.white, opacity: 0.7 }}>Engineering institutional-grade investment frameworks.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px' }}>
          {capabilities.map((c, i) => <CapabilityCard key={`capability-${i}`} {...c} index={i} />)}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;